import { ActionIcon, Group, Text } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import type { MRT_RowData, MRT_TableInstance } from "mantine-react-table-open";
import { useCallback, useEffect, useState } from "react";
import { getScrollState } from "../../utils/getScrollState";
import { getScrollTarget } from "../../utils/getScrollTarget";

interface ScrollControlsProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
}

function ScrollControls<TData extends MRT_RowData>({
  table
}: ScrollControlsProps<TData>) {
  const containerRef = table.refs.tableContainerRef;
  const prefersReducedMotion = useReducedMotion();
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false
  });

  const updateScrollState = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const next = getScrollState(container);
    setScrollState((prev) =>
      prev.canScrollLeft === next.canScrollLeft &&
      prev.canScrollRight === next.canScrollRight
        ? prev
        : next
    );
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    container.addEventListener("scroll", updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);
    Array.from(container.children).forEach((child) =>
      resizeObserver.observe(child)
    );

    return () => {
      container.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [containerRef, updateScrollState]);

  function scrollByStep(direction: "left" | "right") {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const pinnedWidth = table
      .getLeftVisibleLeafColumns()
      .reduce((width, column) => width + column.getSize(), 0);

    container.scrollTo({
      left: getScrollTarget({
        direction,
        scrollLeft: container.scrollLeft,
        clientWidth: container.clientWidth,
        scrollWidth: container.scrollWidth,
        pinnedWidth
      }),
      behavior: getScrollBehavior(prefersReducedMotion)
    });
  }

  return (
    <Group gap="xs" wrap="nowrap">
      <Text fz="sm">Tabelle scrollen</Text>
      <ActionIcon
        aria-disabled={!scrollState.canScrollLeft}
        aria-label="Nach links scrollen"
        data-disabled={!scrollState.canScrollLeft}
        variant="default"
        onClick={() => {
          if (scrollState.canScrollLeft) {
            scrollByStep("left");
          }
        }}
      >
        <IconChevronLeft size={16} />
      </ActionIcon>
      <ActionIcon
        aria-disabled={!scrollState.canScrollRight}
        aria-label="Nach rechts scrollen"
        data-disabled={!scrollState.canScrollRight}
        variant="default"
        onClick={() => {
          if (scrollState.canScrollRight) {
            scrollByStep("right");
          }
        }}
      >
        <IconChevronRight size={16} />
      </ActionIcon>
    </Group>
  );
}

function getScrollBehavior(prefersReducedMotion: boolean): ScrollBehavior {
  return prefersReducedMotion ? "auto" : "smooth";
}

export { ScrollControls };
